/**
 * Frontend decoder for the `headless-bridge` plugin's menu payload.
 *
 * Ingests the untyped JSON returned by
 * `GET /wp-json/{namespace}/menus/{location}` and validates it into the
 * strongly-typed {@link Menu} / {@link MenuItem} tree that `Navbar` and
 * `Footer` consume. Concretely, this module is the client-side inverse of
 * the PHP `Headless_Bridge_Rest_Menus::build_tree()` helper (see
 * `cms/wp-content/plugins/headless-bridge/includes/class-rest-menus.php`):
 * the plugin serialises a nav-menu into
 *
 *   {
 *     "location": "primary",
 *     "items": [
 *       { "id": 12, "label": "Home", "url": "/", "target": "_self",
 *         "slug": "home", "children": [] },
 *       ...
 *     ]
 *   }
 *
 * and this module returns the equivalent TypeScript {@link Menu} value —
 * or throws with a descriptive error if the payload is malformed. The
 * check is deliberately narrow: fields that the design contract mandates
 * (`id`, `label`, `url`, `slug`) are hard-required, while the tolerant
 * fields (`target`, `children`) fall back to their defaults so a menu
 * with a partially populated payload still renders.
 *
 * Property 9 (menu tree serialization) in the feature design exercises
 * the BACKEND `build_tree()` builder against randomly generated forests;
 * this parser is the frontend contract partner for that property. If
 * the two ever drift, the Navbar/Footer would silently render wrong
 * links, so both sides pin the same shape.
 *
 * Validates: Requirements 2.3, 9.4
 *
 * @see .kiro/specs/headless-cms-platform/design.md — CMS Design (Endpoint `/menus/{location}`)
 * @see .kiro/specs/headless-cms-platform/design.md — Property 9 (menu tree serialization)
 * @see cms/wp-content/plugins/headless-bridge/includes/class-rest-menus.php `build_tree()`
 * @module lib/menuTree
 */

import type { Menu, MenuItem } from '@/types';

/**
 * Maximum supported menu-tree nesting depth.
 *
 * Root items sit at depth 0; their children at depth 1; and so on. Items
 * whose depth would exceed {@link MAX_MENU_DEPTH} have their `children`
 * array truncated to `[]`. The cap is defensive: real menus rarely exceed
 * three levels, and an unbounded recursion here would let a malformed
 * payload (or a future backend regression) blow the call stack on the
 * client.
 */
export const MAX_MENU_DEPTH = 5;

/**
 * Type guard: is `value` a plain object (not `null`, not an array)?
 *
 * `typeof null === 'object'` in JavaScript, and arrays are also objects,
 * so a bare `typeof x === 'object'` check is not sufficient before
 * indexing into `x` with string keys. This narrows to the shape the
 * parsing helpers can safely destructure.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Render a short human-readable description of an unknown value for use
 * inside thrown error messages.
 *
 * The goal is to help a developer diagnosing a mismatched payload, not
 * to serialise the value in full — deep objects are reported as their
 * type name to keep the message compact.
 */
function describe(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

/**
 * Emit a dev-only truncation warning when the parser encounters a menu
 * item nested deeper than {@link MAX_MENU_DEPTH}.
 *
 * Silent in production builds so a misconfigured menu does not spam the
 * user's browser console.
 */
function warnDepthTruncation(depth: number): void {
  if (import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.warn(
      `parseMenu: menu items below depth ${MAX_MENU_DEPTH} were truncated ` +
        `(encountered depth ${depth}). Check the nav-menu configuration in WordPress.`,
    );
  }
}

/**
 * Parse and validate a raw JSON payload into a typed {@link Menu}.
 *
 * Throws a `TypeError`-style `Error` (with a descriptive message
 * identifying the offending field) when the payload violates the
 * contract fixed by the design's `Menu` interface. The recursion into
 * items is delegated to {@link parseMenuItem}.
 *
 * @param raw - The unparsed value returned by the REST_API (typically
 *   the JSON body of `GET /menus/{location}`).
 * @throws When `raw` is not an object, when `location` is missing or not
 *   a string, or when `items` is missing or not an array.
 */
export function parseMenu(raw: unknown): Menu {
  if (!isPlainObject(raw)) {
    throw new Error(`parseMenu: expected an object payload, received ${describe(raw)}.`);
  }

  const location = raw.location;
  if (typeof location !== 'string' || location.length === 0) {
    throw new Error(
      `parseMenu: "location" must be a non-empty string, received ${describe(location)}.`,
    );
  }

  const itemsRaw = raw.items;
  if (!Array.isArray(itemsRaw)) {
    throw new Error(`parseMenu: "items" must be an array, received ${describe(itemsRaw)}.`);
  }

  const items = itemsRaw.map((entry) => parseMenuItem(entry, 0));

  return { location, items };
}

/**
 * Parse and validate a single menu item, recursively descending into its
 * `children` array.
 *
 * The strict fields (`id`, `label`, `url`, `slug`) each throw with a
 * descriptive error when missing or of the wrong type. The tolerant
 * fields degrade gracefully:
 *
 * - `target` — any value other than the literal `'_blank'` collapses to
 *   `'_self'`, matching how `Headless_Bridge_Rest_Menus::format_item()`
 *   normalises on the backend.
 * - `children` — a missing or non-array value is treated as `[]`.
 *
 * When descending past {@link MAX_MENU_DEPTH}, deeper children are
 * dropped and a dev-mode warning is logged. The returned item still
 * carries its own metadata; only its `children` array is emptied.
 *
 * @param raw - The unparsed value for this menu item.
 * @param depth - Zero-based depth of this item in the tree. Callers at
 *   the root should omit this argument (or pass `0`); recursive calls
 *   forward `parentDepth + 1`.
 * @throws When `raw` is not an object or when a strict field is missing
 *   or of the wrong type.
 */
export function parseMenuItem(raw: unknown, depth = 0): MenuItem {
  if (!isPlainObject(raw)) {
    throw new Error(`parseMenu: menu item must be an object, received ${describe(raw)}.`);
  }

  const idValue = raw.id;
  if (typeof idValue !== 'number' || !Number.isFinite(idValue)) {
    throw new Error(
      `parseMenu: menu item "id" must be a finite number, received ${describe(idValue)}.`,
    );
  }

  const labelValue = raw.label;
  if (typeof labelValue !== 'string') {
    throw new Error(
      `parseMenu: menu item "label" must be a string, received ${describe(labelValue)}.`,
    );
  }

  const urlValue = raw.url;
  if (typeof urlValue !== 'string') {
    throw new Error(
      `parseMenu: menu item "url" must be a string, received ${describe(urlValue)}.`,
    );
  }

  const slugValue = raw.slug;
  if (typeof slugValue !== 'string') {
    throw new Error(
      `parseMenu: menu item "slug" must be a string, received ${describe(slugValue)}.`,
    );
  }

  // Tolerant: only the exact literal '_blank' opens a new tab; anything
  // else (including the WP default empty string) is a same-tab link.
  const target: MenuItem['target'] = raw.target === '_blank' ? '_blank' : '_self';

  // Tolerant: a missing or non-array `children` collapses to no
  // children. Deeper-than-MAX_MENU_DEPTH children are truncated with a
  // dev-mode warning.
  const childrenRaw = raw.children;
  let children: MenuItem[];
  if (!Array.isArray(childrenRaw) || childrenRaw.length === 0) {
    children = [];
  } else if (depth + 1 > MAX_MENU_DEPTH) {
    warnDepthTruncation(depth + 1);
    children = [];
  } else {
    children = childrenRaw.map((child) => parseMenuItem(child, depth + 1));
  }

  return {
    id: idValue,
    label: labelValue,
    url: urlValue,
    target,
    slug: slugValue,
    children,
  };
}

/**
 * Depth-first flatten a {@link Menu} into a linear list of items.
 *
 * Sibling order is preserved and each parent appears immediately before
 * its descendants (pre-order traversal). Useful for test assertions,
 * client-side search over menu labels, and any consumer that wants a
 * single iterable regardless of the tree shape.
 *
 * @param menu - Parsed menu tree.
 * @returns Flat array of every {@link MenuItem} reachable from the root.
 */
export function flattenMenu(menu: Menu): MenuItem[] {
  const out: MenuItem[] = [];
  const walk = (items: MenuItem[]): void => {
    for (const item of items) {
      out.push(item);
      if (item.children.length > 0) walk(item.children);
    }
  };
  walk(menu.items);
  return out;
}
